<template>
  <main class="product-page" :class="`product-page--${kind}`">
    <section class="product-shell product-intro">
      <nav class="breadcrumb" aria-label="Navegação estrutural">
        <button type="button" @click="router.push('/')">Início</button>
        <span>/</span>
        <span>{{ isRent ? "Rent-a-Car" : "Alojamentos" }}</span>
      </nav>
      <h1>{{ isRent ? "Viaturas para alugar em Maputo e Matola" : "Alojamentos para todos os momentos" }}</h1>
      <section class="search-panel" :aria-label="isRent ? 'Pesquisar viaturas' : 'Pesquisar alojamentos'">
        <nav class="search-tabs" aria-label="Escolher serviço">
          <button type="button" @click="router.push('/imoveis-search')">Imóveis</button>
          <button type="button" :class="{ 'is-active': !isRent }" @click="!isRent || router.push('/alojamentos')">Alojamento</button>
          <button type="button" :class="{ 'is-active': isRent }" @click="isRent || router.push('/rent-a-car')">Rent-a-Car</button>
        </nav>
        <form class="catalog-search" @submit.prevent="applySearch">
          <label class="search-field">
            <span>{{ isRent ? 'Categoria' : 'Tipo de alojamento' }}</span>
            <select v-model="search.category">
              <option value="">Todos</option>
              <option v-for="choice in categoryChoices" :key="choice.value" :value="choice.value">{{ choice.label }}</option>
            </select>
          </label>
          <label class="search-field search-field--location">
            <span>Localização</span>
            <input v-model.trim="search.location" type="search" :placeholder="isRent ? 'Maputo, Matola ou aeroporto' : 'Cidade, praia ou destino'" />
          </label>
          <label v-if="!isRent" class="search-field">
            <span>Hóspedes</span>
            <select v-model="search.guests"><option value="">Todos</option><option v-for="value in [1,2,4,6,8]" :key="value" :value="value">{{ value }}+</option></select>
          </label>
          <label v-else class="search-field">
            <span>Serviço</span>
            <select v-model="search.service"><option value="">Todos</option><option value="self_drive">Sem motorista</option><option value="with_driver">Com motorista</option></select>
          </label>
          <label class="search-field">
            <span>Preço até</span>
            <select v-model="search.maxPrice"><option value="">Sem limite</option><option v-for="value in priceChoices" :key="value" :value="value">{{ Number(value).toLocaleString('pt-MZ') }} MZN</option></select>
          </label>
          <button class="search-submit" type="submit">Pesquisar</button>

          <div v-if="advancedSearch" class="advanced-grid">
            <label class="search-field"><span>Preço desde</span><input v-model.number="search.minPrice" type="number" min="0" placeholder="0 MZN" /></label>
            <template v-if="isRent">
              <label class="search-field"><span>Transmissão</span><select v-model="search.transmission"><option value="">Todas</option><option value="automatic">Automática</option><option value="manual">Manual</option></select></label>
              <label class="search-field"><span>Combustível</span><select v-model="search.fuel"><option value="">Todos</option><option value="petrol">Gasolina</option><option value="diesel">Diesel</option><option value="hybrid">Híbrido</option><option value="electric">Eléctrico</option></select></label>
              <label class="search-field"><span>Lugares</span><select v-model="search.seats"><option value="">Todos</option><option v-for="value in [2,4,5,7,9]" :key="value" :value="value">{{ value }}+</option></select></label>
              <label class="search-check"><input v-model="search.airConditioning" type="checkbox" /><span>Ar condicionado</span></label>
            </template>
          </div>
        </form>
        <div class="search-panel__footer"><button type="button" @click="advancedSearch = !advancedSearch">{{ advancedSearch ? 'Menos filtros' : 'Mais filtros' }} <span aria-hidden="true">{{ advancedSearch ? '−' : '+' }}</span></button></div>
      </section>
    </section>

    <section class="product-shell catalog-results">
      <div class="category-bar" aria-label="Filtros do catálogo">
        <button
          v-for="category in copy.categories"
          :key="category"
          type="button"
          :class="{ 'is-active': activeCategory === category }"
          @click="activeCategory = category"
        >
          {{ category }}
        </button>
      </div>

      <div class="results-heading">
        <p><strong>{{ filteredItems.length }}</strong> {{ copy.countLabel }}</p>
        <div class="results-heading__actions">
          <div class="view-switch" aria-label="Modo de apresentação">
            <button
              type="button"
              :class="{ 'is-active': viewMode === 'grid' }"
              aria-label="Ver em grelha"
              :aria-pressed="viewMode === 'grid'"
              @click="viewMode = 'grid'"
            >
              <span class="view-switch__grid" aria-hidden="true"></span>
            </button>
            <button
              type="button"
              :class="{ 'is-active': viewMode === 'list' }"
              aria-label="Ver em lista"
              :aria-pressed="viewMode === 'list'"
              @click="viewMode = 'list'"
            >
              <span class="view-switch__list" aria-hidden="true"></span>
            </button>
          </div>
          <select v-model="sortOrder" aria-label="Ordenar resultados">
            <option value="recommended">Ordenar: mais recomendados</option>
            <option value="price-asc">Preço: mais baixo</option>
            <option value="price-desc">Preço: mais alto</option>
          </select>
        </div>
      </div>

      <div v-if="loading" class="empty-state"><q-spinner-dots size="48px" color="primary" /><p>A carregar catálogo...</p></div>
      <div v-else-if="error" class="empty-state"><q-icon name="error_outline" size="54px" /><h2>Não foi possível carregar o catálogo</h2><p>{{ error }}</p><button type="button" @click="fetchItems">Tentar novamente</button></div>
      <div
        v-else-if="filteredItems.length"
        class="catalog-grid"
        :class="{ 'catalog-grid--list': viewMode === 'list' }"
      >
        <template v-for="(item, index) in sortedItems" :key="item.slug">
          <article class="catalog-card" tabindex="0" @click="openItem(item.slug)" @keydown.enter="openItem(item.slug)">
            <div class="catalog-card__media">
              <img :src="item.image" :alt="item.title" loading="lazy" />
              <span class="catalog-card__badge">{{ item.badge }}</span>
              <span v-if="item.status" class="catalog-card__status"><i aria-hidden="true"></i>{{ item.status }}</span>
            </div>
            <div class="catalog-card__body">
              <div class="catalog-card__price">
                <strong>{{ item.price }}</strong><small>{{ item.priceUnit }}</small>
                <button type="button" :aria-label="favoriteLabel(item)" @click.stop="toggleFavorite(item.slug)">
                  {{ favorites.includes(favoriteKey(item.slug)) ? "♥" : "♡" }}
                </button>
              </div>
              <h2>{{ item.title }}</h2>
              <p>{{ item.location }}</p>
              <div class="catalog-card__facts">
                <span v-for="fact in item.facts" :key="fact">{{ fact }}</span>
              </div>
              <small class="catalog-card__updated">{{ item.note }}</small>
            </div>
          </article>

          <CarouselAd v-if="index === 3" class="catalog-ad-slot" variant="horizontal" placement-key="catalog_results_inline" />
        </template>
      </div>

      <div v-else class="empty-state">
        <q-icon :name="isRent ? 'directions_car' : 'hotel'" size="54px" />
        <h2>{{ copy.emptyTitle }}</h2>
        <p>Altere o destino ou os filtros para ver mais opções.</p>
        <button type="button" @click="clearSearch">Limpar pesquisa</button>
      </div>
      <div v-if="!loading && pagination.last_page > 1" class="catalog-pagination">
        <q-pagination v-model="page" :max="pagination.last_page" direction-links @update:model-value="fetchItems" />
      </div>
    </section>
  </main>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/config/api";
import CarouselAd from "./CarouselAd.vue";

const props = defineProps({ kind: { type: String, default: "accommodation" } });
const router = useRouter();
const route = useRoute();
const isRent = computed(() => props.kind === "rent");
const activeCategory = ref("Todos");
const sortOrder = ref("recommended");
const viewMode = ref("grid");
const loading = ref(true);
const error = ref("");
const items = ref([]);
const options = ref({ accommodation_types: [], vehicle_categories: [] });
const pagination = ref({ current_page: 1, last_page: 1, total: 0 });
const page = ref(1);
const advancedSearch = ref(false);
const favorites = ref(JSON.parse(localStorage.getItem("goldenkey:catalog-favorites") || "[]"));
const search = reactive({ location: "", category: "", guests: "", service: "", minPrice: "", maxPrice: "", transmission: "", fuel: "", seats: "", airConditioning: false });
const priceChoices = computed(() => isRent.value ? [2500, 5000, 10000, 20000] : [3000, 5000, 10000, 25000, 50000]);

const copy = computed(() => isRent.value ? {
  countLabel: "viaturas disponíveis",
  emptyTitle: "Nenhuma viatura encontrada",
  categories: ["Todos", "Sem motorista", "Com motorista", ...(options.value.vehicle_categories || []).map((item) => item.name)],
} : {
  countLabel: "alojamentos disponíveis",
  emptyTitle: "Nenhum alojamento encontrado",
  categories: ["Todos", ...(options.value.accommodation_types || []).map((item) => item.name)],
});

const categoryChoices = computed(() => isRent.value
  ? (options.value.vehicle_categories || []).map((item) => ({ label: item.name, value: item.id }))
  : (options.value.accommodation_types || []).map((item) => ({ label: item.name, value: item.id })));
const filteredItems = computed(() => items.value);

const sortedItems = computed(() => {
  return filteredItems.value;
});

function formatPrice(value, currency) {
  if (value == null) return "Sob consulta";
  return `${Number(value).toLocaleString("pt-MZ")} ${currency || "MZN"}`;
}
function transformItem(item) {
  if (isRent.value) {
    const services = (item.service_options || []).map((option) => option.service_type === "with_driver" ? "Com motorista" : "Sem motorista");
    return { ...item, priceValue: Number(item.price || 0), price: formatPrice(item.price, item.currency), priceUnit: "/ dia", badge: item.category?.name || "Viatura", status: item.promotion?.badge_label || (item.promotion?.editorial_featured ? "Destaque editorial" : item.verified ? "Verificado" : ""), category: item.category?.name, services, facts: [item.transmission === "automatic" ? "Automático" : "Manual", `${item.seats} lugares`, item.fuel], note: services.join(" · "), image: item.primary_image_url || "https://placehold.co/900x600?text=Golden+Key" };
  }
  return { ...item, priceValue: Number(item.price || 0), price: formatPrice(item.price, item.currency), priceUnit: "/ noite", badge: item.type?.name || "Alojamento", status: item.promotion?.badge_label || (item.promotion?.editorial_featured ? "Destaque editorial" : item.verified ? "Verificado" : ""), category: item.type?.name, facts: [`${item.capacity || 0} hóspedes`, `${item.bedroom_amount || 0} quartos`, `${item.bathroom_amount || 0} banhos`], note: item.type?.name || "Alojamento", image: item.primary_image_url || "https://placehold.co/900x600?text=Golden+Key" };
}
async function fetchOptions() {
  const endpoint = isRent.value ? "/rental-vehicles/options" : "/accommodations/options";
  const response = await apiClient.get(endpoint); options.value = response.data.data;
}
async function fetchItems() {
  loading.value = true; error.value = "";
  try {
    const params = new URLSearchParams({ page: page.value, per_page: 10 });
    params.set("sort", sortOrder.value);
    if (search.location) params.set("location", search.location);
    if (isRent.value) {
      if (search.category) params.set("vehicle_category_id", search.category);
      if (search.service) params.set("service_type", search.service);
      if (search.transmission) params.set("transmission", search.transmission);
      if (search.fuel) params.set("fuel", search.fuel);
      if (search.seats) params.set("seats", search.seats);
      if (search.airConditioning) params.set("air_conditioning", "1");
      if (activeCategory.value === "Sem motorista") params.set("service_type", "self_drive");
      else if (activeCategory.value === "Com motorista") params.set("service_type", "with_driver");
      else if (activeCategory.value !== "Todos") { const category = options.value.vehicle_categories?.find((item) => item.name === activeCategory.value); if (category) params.set("vehicle_category_id", category.id); }
    } else {
      if (search.category) params.set("accommodation_type_id", search.category);
      if (search.guests) params.set("guests", search.guests);
      if (activeCategory.value !== "Todos") { const type = options.value.accommodation_types?.find((item) => item.name === activeCategory.value); if (type) params.set("accommodation_type_id", type.id); }
    }
    if (search.minPrice) params.set("min_price", search.minPrice);
    if (search.maxPrice) params.set("max_price", search.maxPrice);
    const endpoint = isRent.value ? "/rental-vehicles" : "/accommodations";
    const response = await apiClient.get(`${endpoint}?${params.toString()}`);
    const result = response.data.data; items.value = (result.data || []).map(transformItem); pagination.value = result;
  } catch (requestError) { items.value = []; error.value = requestError.response?.data?.message || "Tente novamente."; }
  finally { loading.value = false; }
}
function applySearch() {
  page.value = 1; fetchItems();
  document.querySelector(".catalog-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
}
function hydrateSearchFromRoute() {
  Object.assign(search, {
    location: route.query.location || "",
    category: route.query.accommodation_type_id || route.query.vehicle_category_id || "",
    guests: route.query.guests || "",
    service: route.query.service_type || "",
    minPrice: route.query.min_price || "",
    maxPrice: route.query.max_price || "",
    transmission: route.query.transmission || "",
    fuel: route.query.fuel || "",
    seats: route.query.seats || "",
    airConditioning: route.query.air_conditioning === "1",
  });
}
function clearSearch() { Object.assign(search, { location: "", category: "", guests: "", service: "", minPrice: "", maxPrice: "", transmission: "", fuel: "", seats: "", airConditioning: false }); activeCategory.value = "Todos"; page.value = 1; fetchItems(); }
function openItem(slug) { router.push(isRent.value ? `/rent-a-car/${slug}` : `/alojamentos/${slug}`); }
function favoriteKey(slug) { return `${isRent.value ? "rent" : "accommodation"}:${slug}`; }
function favoriteLabel(item) { return `${favorites.value.includes(favoriteKey(item.slug)) ? "Remover" : "Adicionar"} ${item.title} dos favoritos`; }
function toggleFavorite(slug) {
  const key = favoriteKey(slug);
  favorites.value = favorites.value.includes(key) ? favorites.value.filter((item) => item !== key) : [...favorites.value, key];
  localStorage.setItem("goldenkey:catalog-favorites", JSON.stringify(favorites.value));
}
watch(activeCategory, () => { page.value = 1; fetchItems(); });
watch(sortOrder, () => { page.value = 1; fetchItems(); });
watch(() => props.kind, async () => { activeCategory.value = "Todos"; clearSearch(); await fetchOptions(); await fetchItems(); });
onMounted(async () => { hydrateSearchFromRoute(); try { await fetchOptions(); } catch (requestError) { error.value = requestError.response?.data?.message || "Não foi possível carregar os filtros."; } await fetchItems(); });
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Karla:wght@400;500;600;700&family=Sora:wght@600;700&display=swap");
.product-page { min-height: 100vh; color: #14110e; background: #fff; font-family: "Karla",sans-serif; }
.product-shell { box-sizing: border-box; width: min(100%,1440px); margin-inline: auto; padding-inline: 48px; }
.product-intro { padding-top: 38px; }
.breadcrumb { display: flex; align-items: center; gap: 8px; color: #756e65; font-size: 12px; }
.breadcrumb button { padding: 0; border: 0; color: #7b6134; background: none; cursor: pointer; }
.product-intro h1 { margin: 12px 0 22px; font: 700 32px/39px "Sora",sans-serif; }
.search-panel { padding: 16px 18px; border: 1px solid #e6e0d6; border-radius: 5px; background: #fff; box-shadow: 0 2px 8px rgba(20,17,14,.08); }
.search-tabs { display: flex; gap: 30px; min-height: 30px; margin-bottom: 14px; }
.search-tabs button { position: relative; padding: 0 0 8px; border: 0; color: #756e65; background: none; font: 600 14px/18px "Karla",sans-serif; cursor: pointer; }
.search-tabs button.is-active { color: #14110e; font-weight: 700; }
.search-tabs button.is-active::after { position: absolute; right: 0; bottom: 0; left: 0; height: 2px; background: #eba301; content: ""; }
.catalog-search { display: grid; grid-template-columns: 1fr 1.4fr 1fr 1fr 132px; gap: 20px; align-items: end; color: #14110e; }
.search-field { display: grid; gap: 7px; min-width: 0; }
.search-field > span:first-child { color: #5a5147; font-size: 11px; line-height: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; }
.search-field input,.search-field select { box-sizing: border-box; min-width: 0; width: 100%; height: 52px; padding: 0 36px 0 15px; border: 1px solid #d8d1c6; border-radius: 5px; outline: 0; color: #5a5147; background: #fff; font: 400 15px/15px "Karla",sans-serif; }
.search-field input { padding-right: 15px; }
.search-field input:focus,.search-field select:focus { border-color: #eba301; box-shadow: 0 0 0 2px rgba(235,163,1,.15); }
.search-submit { height: 52px; border: 0; border-radius: 5px; color: #14110e; background: #eba301; font: 700 14px/17px "Karla",sans-serif; cursor: pointer; }
.advanced-grid { grid-column: 1/-1; display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 14px 20px; padding-top: 16px; border-top: 1px solid #eee8df; }
.search-check { display: flex; align-items: center; gap: 9px; min-height: 52px; color: #5a5147; font-size: 13px; }
.search-check input { width: 17px; height: 17px; accent-color: #eba301; }
.search-panel__footer { display: flex; justify-content: flex-end; margin-top: 10px; }
.search-panel__footer button { padding: 4px 0; border: 0; color: #7b6134; background: transparent; font: 600 11px/14px "Karla",sans-serif; cursor: pointer; }
.catalog-results { padding-top: 20px; padding-bottom: 92px; }
.catalog-pagination { display: flex; justify-content: center; padding-top: 32px; }
.product-page--rent .catalog-results { padding-top: 22px; }
.category-bar { display: flex; flex-wrap: wrap; gap: 8px; padding: 0 0 18px; border-bottom: 1px solid #e6e0d6; }
.category-bar button { min-height: 32px; padding: 0 12px; border: 1px solid #e6e0d6; border-radius: 4px; color: #5a5147; background: #fff; font: 600 10px/12px "Karla",sans-serif; cursor: pointer; }
.category-bar button.is-active { border-color: #14110e; color: #fff; background: #14110e; }
.results-heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 16px 0; }
.results-heading p { margin: 0; font-size: 12px; }
.results-heading select { height: 34px; padding: 0 30px 0 10px; border: 1px solid #e6e0d6; border-radius: 5px; color: #5a5147; background: #fff; font-size: 10px; }
.results-heading__actions,.view-switch { display:flex; align-items:center; }
.results-heading__actions { gap:10px; }
.view-switch { gap:8px; }
.view-switch button { width:36px; height:36px; display:grid; place-items:center; padding:0; border:1px solid #ded7ce; color:#756e65; background:#fff; cursor:pointer; }
.view-switch button.is-active { border-color:#14110e; color:#fff; background:#14110e; }
.view-switch button:focus-visible { outline:2px solid #eba301; outline-offset:2px; }
.view-switch__grid { width:12px; height:12px; background:repeating-linear-gradient(90deg,currentColor 0 2px,transparent 2px 5px),repeating-linear-gradient(0deg,currentColor 0 2px,transparent 2px 5px); }
.view-switch__list { width:14px; height:12px; background:repeating-linear-gradient(0deg,currentColor 0 2px,transparent 2px 5px); }
.catalog-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 24px 20px; }
.catalog-grid--list { grid-template-columns:1fr; }
.catalog-grid--list .catalog-card { min-height:240px; display:grid; grid-template-columns:minmax(260px,360px) 1fr; }
.catalog-grid--list .catalog-card__media { height:100%; aspect-ratio:auto; border-right:1px solid #e6e0d6; border-bottom:0; }
.catalog-grid--list .catalog-card__body { min-height:240px; padding:22px 24px; }
.catalog-grid--list .catalog-card h2 { font-size:19px; line-height:24px; white-space:normal; }
.catalog-grid--list .catalog-card__body > p { font-size:14px; }
.catalog-card { min-width: 0; min-height: 420px; border: 1px solid #e6e0d6; border-radius: 5px; background: #fff; box-shadow: 0 2px 8px rgba(20,17,14,.08); overflow: hidden; cursor: pointer; outline: none; }
.catalog-card:focus-visible { box-shadow: 0 0 0 3px rgba(235,163,1,.45); }
.catalog-card__media { position: relative; aspect-ratio: 4/3; border-bottom: 1px solid #e6e0d6; background: #c9c2b6; overflow: hidden; }
.catalog-card__media img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform .25s ease; }
.catalog-card:hover img { transform: scale(1.025); }
.catalog-card__badge,.catalog-card__status { position: absolute; top: 12px; z-index: 2; padding: 5px 9px; border-radius: 999px; font-size: 10px; line-height: 12px; font-weight: 600; }
.catalog-card__badge { left: 12px; color: #fff; background: #14110e; }
.catalog-card__status { right: 12px; display: flex; align-items: center; gap: 5px; color: #14110e; background: #fff; box-shadow: 0 1px 4px rgba(20,17,14,.12); }
.catalog-card__status i { width: 6px; height: 6px; border-radius: 50%; background: #eba301; }
.catalog-card__body { min-height: 176px; display: flex; flex-direction: column; padding: 14px 14px 17px; }
.catalog-card__price { display: flex; align-items: center; gap: 4px; }
.catalog-card__price strong { font: 700 20px/24px "Sora",sans-serif; }
.catalog-card__price small { color: #5a5147; font-size: 10px; }
.catalog-card__price button { width: 36px; height: 36px; margin-left: auto; border: 1px solid #e6e0d6; border-radius: 5px; color: #5a5147; background: #fff; font-size: 20px; cursor: pointer; }
.catalog-card h2 { margin: 7px 0 0; overflow: hidden; font-size: 15px; line-height: 19px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.catalog-card__body > p { margin: 2px 0 0; overflow: hidden; color: #5a5147; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.catalog-card__facts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.catalog-card__facts span { padding: 5px 7px; border-radius: 3px; color: #5a5147; background: #f4f0e8; font-size: 9px; line-height: 11px; }
.catalog-card__updated { margin-top: auto; padding-top: 10px; color: #8a8178; font-size: 10px; }
.catalog-ad-slot { grid-column: 1/-1; justify-self: center; width: min(100%,970px); margin-top: 4px; border-radius: 5px; }
.empty-state { min-height: 420px; display: grid; place-content: center; justify-items: center; color: #756e65; text-align: center; }
.empty-state h2 { margin: 12px 0 4px; color: #14110e; font: 700 20px/26px "Sora",sans-serif; }
.empty-state p { margin: 0 0 14px; }
.empty-state button { min-height: 40px; padding: 0 16px; border: 0; border-radius: 5px; background: #eba301; font-weight: 700; }
@media (max-width:1100px) {
  .product-shell { padding-inline:24px; }
  .catalog-search,.product-page--rent .catalog-search,.advanced-grid { grid-template-columns:repeat(2,minmax(0,1fr)); }
  .search-submit { grid-column:1/-1; }
  .catalog-grid { grid-template-columns:repeat(2,minmax(0,1fr)); }
}
@media (max-width:600px) {
  .product-shell { padding-inline:16px; }
  .product-intro { padding-top:18px; }
  .product-intro h1 { font-size:25px; line-height:31px; }
  .search-panel { padding: 15px; }
  .search-tabs { gap: 22px; overflow-x: auto; }
  .catalog-search,.product-page--rent .catalog-search,.advanced-grid { grid-template-columns:1fr; gap:14px; }
  .search-submit { grid-column:auto; }
  .category-bar { flex-wrap:nowrap; overflow-x:auto; scrollbar-width:none; }
  .category-bar button { flex:0 0 auto; }
  .results-heading { align-items:flex-start; flex-direction:column; }
  .results-heading__actions { width:100%; justify-content:space-between; }
  .results-heading select { flex:1; }
  .catalog-grid { grid-template-columns:1fr; }
  .catalog-grid--list .catalog-card { min-height:190px; grid-template-columns:124px minmax(0,1fr); }
  .catalog-grid--list .catalog-card__media { height:100%; aspect-ratio:auto; border-right:1px solid #e6e0d6; border-bottom:0; }
  .catalog-grid--list .catalog-card__body { min-width:0; min-height:190px; padding:12px; }
  .catalog-grid--list .catalog-card__price strong { font-size:15px; line-height:19px; }
  .catalog-grid--list .catalog-card__price button { width:30px; height:30px; font-size:18px; }
  .catalog-grid--list .catalog-card h2 { display:-webkit-box; font-size:13px; line-height:17px; white-space:normal; -webkit-box-orient:vertical; -webkit-line-clamp:2; line-clamp:2; }
  .catalog-grid--list .catalog-card__body > p { font-size:11px; }
  .catalog-grid--list .catalog-card__facts { gap:4px; margin-top:7px; }
  .catalog-grid--list .catalog-card__facts span { padding:4px 5px; font-size:8px; }
  .catalog-grid--list .catalog-card__status { display:none; }
  .catalog-grid--list .catalog-card__badge { top:8px; left:8px; padding:4px 7px; font-size:9px; }
}
@media (prefers-reduced-motion:reduce) { .catalog-card__media img { transition:none; } }
</style>
